import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';
import { Edit, X, Calendar } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import './AppointmentsPage.css';

/**
 * Appointments page - Scheduling system.
 * Staff can create/manage appointments, clients can only see their own.
 */
export const AppointmentsPage = () => {
  const { user } = useEntitlements();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [viewingAppointment, setViewingAppointment] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    meeting_type: 'internal',
    client: '',
    location: '',
    external_link: '',
    participant_ids: [],
  });
  const [requestData, setRequestData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
  });

  const isStaff = user?.is_staff_user || user?.role === 'staff' || user?.role === 'admin';
  const isClient = user?.is_client_user || user?.role === 'client';

  useEffect(() => {
    fetchAppointments();
    if (isStaff) {
      fetchClients();
    }
  }, [isStaff]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/appointments/');
      setAppointments(response.data.results || response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/v1/clients/');
      setClients(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    try {
      // Prepare data - only include client if meeting_type is 'client'
      const submitData = { ...formData };
      if (submitData.meeting_type !== 'client') {
        submitData.client = '';
      }
      // Remove empty client field if not needed
      if (!submitData.client) {
        delete submitData.client;
      }

      // Convert datetime-local values to ISO 8601 format
      // datetime-local input provides "YYYY-MM-DDTHH:mm" in local time
      // We need to convert to ISO string for the API
      if (submitData.start_time) {
        submitData.start_time = new Date(submitData.start_time).toISOString();
      }
      if (submitData.end_time) {
        submitData.end_time = new Date(submitData.end_time).toISOString();
      }

      await api.post('/v1/appointments/', submitData);
      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        start_time: '',
        end_time: '',
        meeting_type: 'internal',
        client: '',
        location: '',
        external_link: '',
        participant_ids: [],
      });
      fetchAppointments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create appointment');
    }
  };

  const handleEditClick = (appointment) => {
    // Format datetime for datetime-local input (YYYY-MM-DDTHH:mm)
    const formatForInput = (dateTime) => {
      const date = new Date(dateTime);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    setEditingAppointment(appointment);
    setFormData({
      title: appointment.title || '',
      description: appointment.description || '',
      start_time: formatForInput(appointment.start_time),
      end_time: formatForInput(appointment.end_time),
      meeting_type: appointment.meeting_type || 'internal',
      client: appointment.client || '',
      location: appointment.location || '',
      external_link: appointment.external_link || '',
      participant_ids: appointment.participants || [],
    });
    setShowEditModal(true);
  };

  const handleUpdateAppointment = async (e) => {
    e.preventDefault();
    try {
      // Prepare data - only include client if meeting_type is 'client'
      const submitData = { ...formData };
      if (submitData.meeting_type !== 'client') {
        submitData.client = '';
      }
      // Remove empty client field if not needed
      if (!submitData.client) {
        delete submitData.client;
      }

      // Convert datetime-local values to ISO 8601 format
      if (submitData.start_time) {
        submitData.start_time = new Date(submitData.start_time).toISOString();
      }
      if (submitData.end_time) {
        submitData.end_time = new Date(submitData.end_time).toISOString();
      }

      await api.put(`/v1/appointments/${editingAppointment.id}/`, submitData);
      setShowEditModal(false);
      setEditingAppointment(null);
      setFormData({
        title: '',
        description: '',
        start_time: '',
        end_time: '',
        meeting_type: 'internal',
        client: '',
        location: '',
        external_link: '',
        participant_ids: [],
      });
      fetchAppointments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update appointment');
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }
    try {
      await api.post(`/v1/appointments/${appointmentId}/cancel/`);
      fetchAppointments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel appointment');
    }
  };

  const handleRequestAppointment = async (e) => {
    e.preventDefault();
    try {
      // Convert datetime-local values to ISO 8601 format
      const submitData = { ...requestData };
      if (submitData.start_time) {
        submitData.start_time = new Date(submitData.start_time).toISOString();
      }
      if (submitData.end_time) {
        submitData.end_time = new Date(submitData.end_time).toISOString();
      }

      await api.post('/v1/appointments/request_appointment/', submitData);
      setShowRequestModal(false);
      setRequestData({
        title: '',
        description: '',
        start_time: '',
        end_time: '',
      });
      setError(null);
      fetchAppointments();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to request appointment');
    }
  };

  const handleApproveAppointment = async (appointmentId) => {
    try {
      await api.post(`/v1/appointments/${appointmentId}/approve/`);
      fetchAppointments();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to approve appointment');
    }
  };

  const handleRejectAppointment = async (appointmentId) => {
    const reason = window.prompt('Reason for rejection (optional):');
    try {
      await api.post(`/v1/appointments/${appointmentId}/reject/`, { reason });
      fetchAppointments();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to reject appointment');
    }
  };

  const formatDateTime = (dateTime) => {
    // Parse the datetime and format it in the user's local timezone
    const date = new Date(dateTime);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusVariant = (status) => {
    const map = {
      requested: 'warning',
      scheduled: 'accent',
      confirmed: 'success',
      cancelled: 'neutral',
      completed: 'success',
      no_show: 'danger',
    };
    return map[status] || 'neutral';
  };

  if (loading) {
    return <div className="appointments-loading">Loading appointments...</div>;
  }

  return (
    <div className="appointments-page">
      <PageHeader
        icon={Calendar}
        title="Appointments"
        subtitle="Manage schedules, meeting requests, and status updates."
        primaryAction={isStaff ? (
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Schedule Appointment
          </button>
        ) : null}
        secondaryAction={isClient ? (
          <button
            className="btn btn-secondary"
            onClick={() => setShowRequestModal(true)}
          >
            + Request Appointment
          </button>
        ) : null}
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="appointments-calendar">
        {appointments.length === 0 ? (
          <div className="appointments-empty">
            <p>No appointments scheduled yet.</p>
            {isStaff && (
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                Schedule your first appointment
              </button>
            )}
          </div>
        ) : (
          <div className="appointments-list">
            {appointments.map((appointment) => (
              <div
                key={appointment.id}
                className="appointment-card"
                onClick={() => setViewingAppointment(appointment)}
                style={{ cursor: 'pointer' }}
              >
                <div className="appointment-header">
                  <h3>{appointment.title}</h3>
                  <span
                    className="appointment-status"
                  >
                    <StatusBadge
                      label={appointment.status.replace('_', ' ')}
                      variant={getStatusVariant(appointment.status)}
                    />
                  </span>
                </div>
                {appointment.description && (
                  <p className="appointment-description">{appointment.description}</p>
                )}
                <div className="appointment-details">
                  <div className="appointment-time">
                    <strong>Time:</strong> {formatDateTime(appointment.start_time)} - {formatDateTime(appointment.end_time)}
                  </div>
                  {appointment.duration_minutes && (
                    <div className="appointment-duration">
                      Duration: {appointment.duration_minutes} minutes
                    </div>
                  )}
                  {appointment.organizer_name && (
                    <div className="appointment-organizer">
                      Organizer: {appointment.organizer_name}
                    </div>
                  )}
                  {appointment.client_name && (
                    <div className="appointment-client">
                      Client: {appointment.client_name}
                    </div>
                  )}
                  {appointment.participant_emails && appointment.participant_emails.length > 0 && (
                    <div className="appointment-participants">
                      Participants: {appointment.participant_emails.join(', ')}
                    </div>
                  )}
                  {appointment.location && (
                    <div className="appointment-location">
                      Location: {appointment.location}
                    </div>
                  )}
                  {appointment.external_link && (
                    <div className="appointment-link">
                      <a href={appointment.external_link} target="_blank" rel="noopener noreferrer">
                        Join Meeting →
                      </a>
                    </div>
                  )}
                </div>
                {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
                  <div className="appointment-actions" onClick={(e) => e.stopPropagation()}>
                    {isStaff && appointment.status === 'requested' && (
                      <>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleApproveAppointment(appointment.id)}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRejectAppointment(appointment.id)}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {isStaff && appointment.status !== 'requested' && (
                      <>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditClick(appointment)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleCancelAppointment(appointment.id)}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {appointment.external_link && appointment.status !== 'requested' && (
                      <a
                        href={appointment.external_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-primary"
                      >
                        Join Meeting
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Appointment Detail Side Panel */}
      {viewingAppointment && (
        <div className="detail-panel-overlay" onClick={() => setViewingAppointment(null)}>
          <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
            <div className="detail-panel-header">
              <h2>{viewingAppointment.title}</h2>
              <button
                className="detail-panel-close"
                onClick={() => setViewingAppointment(null)}
                title="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="detail-panel-body">
              <section className="detail-section">
                <div className="detail-field" style={{ marginBottom: '1rem' }}>
                  <label>Status</label>
                  <span
                    className="appointment-status"
                  >
                    <StatusBadge
                      label={viewingAppointment.status.replace('_', ' ')}
                      variant={getStatusVariant(viewingAppointment.status)}
                    />
                  </span>
                </div>
                {viewingAppointment.description && (
                  <div className="detail-field detail-field-full">
                    <label>Description</label>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{viewingAppointment.description}</span>
                  </div>
                )}
                <div className="detail-grid">
                  <div className="detail-field detail-field-full">
                    <label>Time</label>
                    <span>
                      {formatDateTime(viewingAppointment.start_time)} - {formatDateTime(viewingAppointment.end_time)}
                    </span>
                  </div>
                  {viewingAppointment.duration_minutes && (
                    <div className="detail-field">
                      <label>Duration</label>
                      <span>{viewingAppointment.duration_minutes} minutes</span>
                    </div>
                  )}
                  {viewingAppointment.organizer_name && (
                    <div className="detail-field">
                      <label>Organizer</label>
                      <span>{viewingAppointment.organizer_name}</span>
                    </div>
                  )}
                  {viewingAppointment.client_name && (
                    <div className="detail-field">
                      <label>Client</label>
                      <span>{viewingAppointment.client_name}</span>
                    </div>
                  )}
                  {viewingAppointment.location && (
                    <div className="detail-field detail-field-full">
                      <label>Location</label>
                      <span>{viewingAppointment.location}</span>
                    </div>
                  )}
                  {viewingAppointment.participant_emails && viewingAppointment.participant_emails.length > 0 && (
                    <div className="detail-field detail-field-full">
                      <label>Participants</label>
                      <span>{viewingAppointment.participant_emails.join(', ')}</span>
                    </div>
                  )}
                </div>
                {viewingAppointment.external_link && (
                  <div className="detail-field" style={{ marginTop: '1rem' }}>
                    <a
                      href={viewingAppointment.external_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="detail-panel-link"
                    >
                      Join Meeting
                    </a>
                  </div>
                )}
                <div className="detail-panel-actions">
                  {isStaff && (
                    <>
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setViewingAppointment(null);
                          handleEditClick(viewingAppointment);
                        }}
                      >
                        <Edit size={16} />
                        Edit Appointment
                      </button>
                      {viewingAppointment.status !== 'cancelled' &&
                        viewingAppointment.status !== 'completed' && (
                          <button
                            className="btn btn-danger"
                            onClick={() => {
                              handleCancelAppointment(viewingAppointment.id);
                              setViewingAppointment(null);
                            }}
                          >
                            Cancel
                          </button>
                        )}
                    </>
                  )}
                  {isStaff &&
                    viewingAppointment.status === 'requested' && (
                      <>
                        <button
                          className="btn btn-success"
                          onClick={() => {
                            handleApproveAppointment(viewingAppointment.id);
                            setViewingAppointment(null);
                          }}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => {
                            handleRejectAppointment(viewingAppointment.id);
                            setViewingAppointment(null);
                          }}
                        >
                          Reject
                        </button>
                      </>
                    )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Schedule Appointment</h2>
            <form onSubmit={handleCreateAppointment}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Start Time *</label>
                <input
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Time *</label>
                <input
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Meeting Type</label>
                <select
                  value={formData.meeting_type}
                  onChange={(e) => {
                    const newMeetingType = e.target.value;
                    setFormData({ 
                      ...formData, 
                      meeting_type: newMeetingType,
                      // Clear client if not a client meeting
                      client: newMeetingType === 'client' ? formData.client : ''
                    });
                  }}
                >
                  <option value="internal">Internal</option>
                  <option value="client">Client Meeting</option>
                  <option value="external">External</option>
                </select>
              </div>
              {formData.meeting_type === 'client' && (
                <div className="form-group">
                  <label>Client *</label>
                  <select
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                    required
                  >
                    <option value="">Select a client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>External Link (Zoom, Teams, etc.)</label>
                <input
                  type="url"
                  value={formData.external_link}
                  onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingAppointment && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Appointment</h2>
            <form onSubmit={handleUpdateAppointment}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Start Time *</label>
                <input
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Time *</label>
                <input
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Meeting Type</label>
                <select
                  value={formData.meeting_type}
                  onChange={(e) => {
                    const newMeetingType = e.target.value;
                    setFormData({
                      ...formData,
                      meeting_type: newMeetingType,
                      // Clear client if not a client meeting
                      client: newMeetingType === 'client' ? formData.client : ''
                    });
                  }}
                >
                  <option value="internal">Internal</option>
                  <option value="client">Client Meeting</option>
                  <option value="external">External</option>
                </select>
              </div>
              {formData.meeting_type === 'client' && (
                <div className="form-group">
                  <label>Client *</label>
                  <select
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                    required
                  >
                    <option value="">Select a client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>External Link (Zoom, Teams, etc.)</label>
                <input
                  type="url"
                  value={formData.external_link}
                  onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => {
                  setShowEditModal(false);
                  setEditingAppointment(null);
                  setFormData({
                    title: '',
                    description: '',
                    start_time: '',
                    end_time: '',
                    meeting_type: 'internal',
                    client: '',
                    location: '',
                    external_link: '',
                    participant_ids: [],
                  });
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Appointment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRequestModal && (
        <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Request Appointment</h2>
            <p className="modal-description">
              Submit a request for an appointment. Our team will review and confirm the appointment details with you.
            </p>
            <form onSubmit={handleRequestAppointment}>
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={requestData.title}
                  onChange={(e) => setRequestData({ ...requestData, title: e.target.value })}
                  required
                  placeholder="What would you like to discuss?"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={requestData.description}
                  onChange={(e) => setRequestData({ ...requestData, description: e.target.value })}
                  rows="4"
                  placeholder="Provide details about what you'd like to discuss..."
                />
              </div>
              <div className="form-group">
                <label>Preferred Start Time *</label>
                <input
                  type="datetime-local"
                  value={requestData.start_time}
                  onChange={(e) => setRequestData({ ...requestData, start_time: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Preferred End Time *</label>
                <input
                  type="datetime-local"
                  value={requestData.end_time}
                  onChange={(e) => setRequestData({ ...requestData, end_time: e.target.value })}
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => {
                  setShowRequestModal(false);
                  setRequestData({
                    title: '',
                    description: '',
                    start_time: '',
                    end_time: '',
                  });
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

